import { useState } from "react";

interface FocusArea {
  id: number;
  title: string;
  priority: "High" | "Medium" | "Low";
  progress: number;
  notes: string;
  updated: string;
  editing: boolean;
}

export function FocusAreas() {
  const [areas, setAreas] = useState<FocusArea[]>([
    {
      id: 1,
      title: "Inferno B-site Holds",
      priority: "High",
      progress: 40,
      notes: "",
      updated: "2 days ago",
      editing: false,
    },
  ]);

  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState("Priority");

  function addFocusArea() {
    if (!newTitle || newPriority === "Priority") return;

    setAreas((prev) => [
      ...prev,
      {
        id: Date.now(),
        title: newTitle,
        priority: newPriority as "High" | "Medium" | "Low",
        progress: 0,
        notes: "",
        updated: "Just now",
        editing: false,
      },
    ]);

    setNewTitle("");
    setNewPriority("Priority");
  }

  function toggleEdit(id: number) {
    setAreas((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, editing: !a.editing } : a
      )
    );
  }

  function updateArea(
    id: number,
    field: keyof FocusArea,
    value: FocusArea[keyof FocusArea]
  ) {
    setAreas((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, [field]: value, updated: "Just now" } : a
      )
    );
  }

  return (
    <div className="space-y-10">

      {/* PAGE HEADER */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Focus Areas</h1>
        <p className="text-muted">
          Track your team’s improvement goals, progress, and priorities.
        </p>
      </div>

      {/* ADD NEW FOCUS AREA */}
      <section className="bg-surface border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Add New Focus Area</h2>

        <div className="flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder="e.g., Improve B-site holds on Inferno"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className="
              flex-grow bg-bg border border-border rounded-md p-3
              focus:outline-none focus:border-[#4FC3F7]
            "
          />

          <select
            value={newPriority}
            onChange={(e) => setNewPriority(e.target.value)}
            className="
              bg-bg border border-border rounded-md p-3
              focus:outline-none focus:border-[#4FC3F7]
            "
          >
            <option>Priority</option>
            <option>High</option>
            <option>Medium</option>
            <option>Low</option>
          </select>

          <button
            onClick={addFocusArea}
            className="
              px-6 py-3 rounded-md font-semibold
              bg-[#4FC3F7] text-[#0C0F14]
              hover:bg-[#81D4FA] transition
            "
          >
            Add
          </button>
        </div>
      </section>

      {/* FOCUS AREA GRID */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">

        {areas.map((area) => (
          <section
            key={area.id}
            className="bg-surface border border-border rounded-lg p-6"
          >
            {/* TITLE + PRIORITY */}
            <div className="flex justify-between items-start mb-4">

              {/* Editable Title */}
              {area.editing ? (
                <input
                  value={area.title}
                  onChange={(e) =>
                    updateArea(area.id, "title", e.target.value)
                  }
                  className="
                    bg-bg border border-border rounded-md p-2
                    focus:outline-none focus:border-[#4FC3F7]
                    w-full mr-3
                  "
                />
              ) : (
                <h2 className="text-lg font-semibold">{area.title}</h2>
              )}

              {/* Editable Priority */}
              {area.editing ? (
                <select
                  value={area.priority}
                  onChange={(e) =>
                    updateArea(area.id, "priority", e.target.value as "High" | "Medium" | "Low")
                  }
                  className="
                    bg-bg border border-border rounded-md p-2
                    focus:outline-none focus:border-[#4FC3F7]
                  "
                >
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              ) : (
                <span
                  className={
                    area.priority === "High"
                      ? "text-red-400 text-sm font-medium"
                      : area.priority === "Medium"
                      ? "text-yellow-400 text-sm font-medium"
                      : "text-green-400 text-sm font-medium"
                  }
                >
                  {area.priority}
                </span>
              )}
            </div>

            {/* PROGRESS */}
            <div className="mb-4">
              <p className="text-sm text-muted mb-1">Progress</p>

              <input
                type="range"
                min="0"
                max="100"
                value={area.progress}
                onChange={(e) =>
                  updateArea(area.id, "progress", Number(e.target.value))
                }
                className="w-full accent-[#4FC3F7]"
              />

              <p className="text-xs text-muted mt-1">
                {area.progress}% complete
              </p>
            </div>

            {/* NOTES */}
            <textarea
              placeholder="Notes about this focus area..."
              value={area.notes}
              onChange={(e) =>
                updateArea(area.id, "notes", e.target.value)
              }
              className="
                w-full min-h-28 bg-bg border border-border rounded-md p-3
                focus:outline-none focus:border-[#4FC3F7]
                resize-y
              "
            />

            {/* FOOTER */}
            <div className="flex justify-between items-center mt-4 text-xs text-muted">
              <p>Last updated: {area.updated}</p>

              <button
                onClick={() => toggleEdit(area.id)}
                className="hover:text-[#4FC3F7] transition"
              >
                {area.editing ? "Save" : "Edit"}
              </button>
            </div>
          </section>
        ))}

      </div>
    </div>
  );
}